import React from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyboardArrowDown, KeyboardArrowUp, KeyboardArrowRight, Error, ReportProblem, CheckCircle } from '@mui/icons-material';
import { capitalizeString, styleThousandCommas, getMembershipStatusColor, planRecurrenceMap, buildRequirementStatusDisplay, countEffectivelyMetRequirements, getMembershipCardKey } from '../../utils/helpers';
import { CurrentRequirementsSchema, MembershipSchema, ExpandedMembershipSchema, AddRequirementStatus } from '../../data/types';

type MembershipCredentialsMobileProps = {
  memberships: MembershipSchema[];
  expandedMemberships: { [key: string]: ExpandedMembershipSchema };
  /** Optional `?id=` query (community_id value) used as default expand key when it matches a membership */
  membershipQuery?: string | null;
  toggleMembership: (communityId: string) => void;
  toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>;
  handleSetViewCommunityResources: (communityId: string, isOpen: boolean) => void;
  handleManageMembership: (membership: MembershipSchema) => void;
};

const MembershipCredentialsMobile: React.FC<MembershipCredentialsMobileProps> = ({
  memberships,
  expandedMemberships,
  membershipQuery,
  toggleMembership,
  toggleAddingCredential,
  handleSetViewCommunityResources,
  handleManageMembership,
}) => {
  const navigate = useNavigate();

  return (
    <div className="w-full">
      {/* Header - positioned above the bordered container */}
      <div className="flex justify-between items-center mb-4 min-w-0">
        <p className="text-sm font-raleway text-[#4D4D4D]/90 font-medium truncate flex-1 mr-2">
          Showing 1 - {memberships.length} of {memberships.length} all memberships
        </p>
        <button className="p-2 flex-shrink-0 bg-white border border-[#E9E9E9] rounded-lg hover:bg-gray-50 transition-colors">
          <svg className="w-5 h-5 text-[#4D4D4D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Membership Cards */}
      <div className="space-y-4">
        {memberships.map((membership, index) => {
          const membershipKey = getMembershipCardKey(membership, membershipQuery) || `membership-${index}`;
          const expandedMembership = expandedMemberships[membershipKey];
          const metRequirementsCount = expandedMembership?.requirementsStatus
            ? countEffectivelyMetRequirements(expandedMembership.requirementsStatus)
            : expandedMembership?.metRequirementsCount || 0;

          return (
            <div key={index} className=" border border-[#E9E9E9] rounded-xl shadow-sm overflow-hidden">
              <div
                onClick={() => toggleMembership(membershipKey)}
                className={`p-4 cursor-pointer transition-colors ${expandedMembership?.isExpanded ? 'bg-[#FAFAFA]' : 'bg-white hover:bg-gray-50'}`}
              >
              {/* Logo and Basic Info */}
              <div className="flex flex-col md:flex-row items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-white rounded-lg border">
                  <img src={membership.logo} alt={membership.name} className="h-8 w-8 object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-raleway font-bold text-lg leading-tight text-primaryDark-0 mb-1 break-words">
                    {membership.name}
                  </h3>
                  <p className="text-xs font-open-sans text-[#4D4D4D] leading-relaxed break-words">
                    {membership.description}
                  </p>
                </div>
              </div>

              {/* Status and Plan Badges */}
              <div className="flex flex-row md:flex-col gap-2 mb-3">
                <span 
                  className="px-5 py-1 align-middle text-center place-self-center rounded-md  text-sm font-raleway font-semibold w-fit"
                  style={{
                    backgroundColor: getMembershipStatusColor(membership.status || 'unknown').bgColor,
                    color: getMembershipStatusColor(membership.status || 'unknown').primaryTextColor
                  }}
                >
                  {capitalizeString(membership.status || 'Unknown')}
                </span>
                <span className="px-3 py-1 rounded-xl text-center text-xs font-raleway font-medium bg-white border border-[#E9E9E9] text-[#4D4D4D] w-fit">
                  {membership.plan_name} Plan (${styleThousandCommas(membership?.plan_rate || 0)} {membership.plan_recurrence && `/ ${planRecurrenceMap[membership.plan_recurrence as keyof typeof planRecurrenceMap]}`})
                </span>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mt-4" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => handleSetViewCommunityResources(membership?.community_id || '', true)}
                  className="flex-1 bg-white border border-[#E9E9E9] rounded-lg px-4 py-2 flex items-center justify-center gap-2 text-sm text-[#4D4D4D] hover:bg-gray-50 min-w-0"
                >
                  <span className="truncate">View Resources</span>
                  <KeyboardArrowRight className="w-4 h-4 flex-shrink-0" />
                </button>
                {membership.status && (
                  <button
                    onClick={() => handleManageMembership(membership)}
                    className="flex-1 bg-white border-[1px] border-primaryDark-0/30 rounded-lg px-4 py-2 flex items-center justify-center gap-2 text-sm font-raleway font-semibold text-primaryDark-0 hover:bg-gray-50 min-w-0"
                  >
                    <span className="truncate">Manage Membership</span>
                  </button>
                )}
                <button
                  onClick={() => navigate('/my-bookings')}
                  className="flex-1 bg-primaryDark-0 text-white rounded-lg px-4 py-2 flex items-center justify-center gap-2 text-sm font-raleway font-medium hover:bg-primaryDark-300 min-w-0"
                >
                  <span className="truncate">View Bookings</span>
                  <KeyboardArrowRight className="w-4 h-4 flex-shrink-0" />
                </button>
              </div>

              {/* Requirements Met */}
              <div className="mt-4">
                <div
                  className="w-full bg-gray-50 border border-[#E9E9E9] rounded-lg px-3 py-2 flex items-center justify-between text-sm text-[#4D4D4D]/70 min-w-0"
                >
                  <span className="truncate">
                    {metRequirementsCount}/{expandedMembership?.totalRequirementsCount || 0} Requirements Met
                  </span>
                  {expandedMembership?.isExpanded ? (
                    <KeyboardArrowUp className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <KeyboardArrowDown className="w-4 h-4 flex-shrink-0" />
                  )}
                </div>
              </div>
              </div>

              {/* Expanded Requirements Section */}
              {expandedMembership?.isExpanded && (
                <div className="p-4 pt-2 border-t border-[#E9E9E9]">
                  <RequiredCredentialsMobile 
                    membershipRequirementsStatus={expandedMembership.requirementsStatus} 
                    metRequirementsCount={expandedMembership.metRequirementsCount} 
                    totalRequirementsCount={expandedMembership.totalRequirementsCount} 
                    toggleAddingCredential={toggleAddingCredential} 
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

type RequiredCredentialsMobileProps = {
  membershipRequirementsStatus: CurrentRequirementsSchema[];
  metRequirementsCount: number;
  totalRequirementsCount: number;
  toggleAddingCredential: React.Dispatch<React.SetStateAction<AddRequirementStatus>>;
};

const RequiredCredentialsMobile: React.FC<RequiredCredentialsMobileProps> = ({ 
  membershipRequirementsStatus, 
  metRequirementsCount, 
  totalRequirementsCount, 
  toggleAddingCredential 
}) => {
  const navigate = useNavigate();
  const computedMetRequirementsCount = membershipRequirementsStatus
    ? countEffectivelyMetRequirements(membershipRequirementsStatus)
    : metRequirementsCount;

  return (
    <div className="space-y-4">
      {/* Progress Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm font-open-sans text-[#808080]">
          {membershipRequirementsStatus?.length > 0 
            ? 'Complete your membership by adding required credentials.' 
            : 'No credentials required for this membership.'
          }
        </p>
        {totalRequirementsCount > 0 && (
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-end">
              <p className="text-xs font-raleway text-black font-medium">Progress</p>
              <div className="w-20 h-2 bg-gray-300 rounded-full">
                <div
                  className="h-2 bg-primaryDark-300 rounded-full"
                  style={{ width: `${(computedMetRequirementsCount / totalRequirementsCount) * 100}%` }}
                />
              </div>
            </div>
            <p className="text-xs font-raleway font-medium">
              {totalRequirementsCount ? Math.round((computedMetRequirementsCount / totalRequirementsCount) * 100) : 0}%
            </p>
          </div>
        )}
      </div>

      {/* Requirements List */}
      <div className="space-y-3">
        {membershipRequirementsStatus?.map((requirement, i) => {
          if (!requirement.is_required) return null;

          const statusDisplay = buildRequirementStatusDisplay(requirement);
          const StatusIcon =
            statusDisplay.statusTone === 'success'
              ? CheckCircle
              : statusDisplay.statusTone === 'warning'
                ? ReportProblem
                : statusDisplay.statusTone === 'error'
                  ? Error
                  : null;
          const statusIconColor =
            statusDisplay.statusTone === 'success'
              ? 'success'
              : statusDisplay.statusTone === 'warning'
                ? 'warning'
                : 'error';
          
          return (
            <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border-[1px] ${statusDisplay.rowSurfaceClass}`}>
              <div className="flex-shrink-0 mt-0.5">
                {statusDisplay.isEffectivelyMet ? (
                  <div className="w-5 h-5 bg-primaryDark-300 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ) : (
                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                )}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-raleway text-black font-semibold">
                  {requirement.name}
                </p>
                <p className="text-xs text-[#4D4D4D] mt-1">
                  {requirement.description}
                </p>
                {statusDisplay.detailLabel && (
                  <p className={`text-xs font-open-sans mt-1 ${statusDisplay.subtitleTextClass}`}>
                    {statusDisplay.detailLabel}
                  </p>
                )}
                {statusDisplay.statusLabel && StatusIcon && (
                  <div className="flex flex-row items-center mt-1">
                    <p className={`text-xs font-open-sans font-medium mr-1 ${statusDisplay.statusTextClass}`}>
                      {statusDisplay.statusLabel}
                    </p>
                    <StatusIcon sx={{ fontSize: 14 }} color={statusIconColor as 'success' | 'warning' | 'error'} />
                  </div>
                )}
              </div>
              
              <div className="flex-shrink-0">
                {requirement.code === "PAY" ? (
                  <button 
                    onClick={() => navigate('/wallet')} 
                    className="bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-xs rounded-md px-3 py-2"
                  >
                    Go to Payment
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button 
                      onClick={() => toggleAddingCredential({ status: true, requirement })} 
                      className="bg-primaryDark-0 hover:bg-primaryDark-300 text-white font-raleway font-bold text-xs rounded-md px-3 py-2"
                    >
                      {(requirement?.is_required && requirement?.presigned_url) ? 'Change' : 'Upload'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MembershipCredentialsMobile;
